import { useState } from "react";
import { Heart, HandHeart, Megaphone, Truck } from "lucide-react";
import { PageHero, SectionHead } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { useSubmit } from "../lib/api";
import { useTitle } from "../lib/useTitle";

const ROLES = ["Gardening", "Cooking & Meals", "Health Coaching Support", "Vocational Mentoring", "Events & Logistics", "Prayer Team"];

const WAYS = [
  { icon: HandHeart, title: "Volunteer", text: "Give your hands and heart — in the garden, the kitchen, or the classroom." },
  { icon: Heart, title: "Give", text: "Sow financially into a woman's three-year journey toward self-sufficiency." },
  { icon: Megaphone, title: "Advocate", text: "Share our story with your church, workplace, and neighborhood." },
  { icon: Truck, title: "Partner", text: "Businesses and churches can provide supplies, venues, and training." },
];

export default function GetInvolved() {
  useTitle("Get Involved | Bee Courageous @ Garden Grub");
  const volunteer = useSubmit("/submissions");
  const application = useSubmit("/submissions");

  const [vForm, setVForm] = useState({ name: "", email: "", phone: "", availability: "", message: "" });
  const [roles, setRoles] = useState([]);
  const [aForm, setAForm] = useState({ name: "", email: "", phone: "", situation: "", heard: "", safeContact: true });

  const toggleRole = (r) => setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const onVolunteer = async (e) => {
    e.preventDefault();
    const res = await volunteer.submit({
      type: "volunteer",
      name: vForm.name,
      email: vForm.email,
      phone: vForm.phone || undefined,
      details: { roles, availability: vForm.availability, message: vForm.message },
    });
    if (res?.success) {
      setVForm({ name: "", email: "", phone: "", availability: "", message: "" });
      setRoles([]);
    }
  };

  const onApply = async (e) => {
    e.preventDefault();
    const res = await application.submit({
      type: "application",
      name: aForm.name,
      email: aForm.email,
      phone: aForm.phone || undefined,
      details: { situation: aForm.situation, heard: aForm.heard, safe_to_contact: aForm.safeContact },
    });
    if (res?.success) setAForm({ name: "", email: "", phone: "", situation: "", heard: "", safeContact: true });
  };

  return (
    <>
      <PageHero
        eyebrow="Get Involved"
        lines={["Many hands.", "One garden of hope."]}
        lede="Whether you are seeking a place to heal or a place to serve, there is room for you here. Choose your path below."
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {WAYS.map((w, i) => (
            <Reveal key={w.title} delay={0.07 * i}>
              <article className="card-soft h-full text-center" data-testid={`way-${i}`}>
                <span className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-pollen-soft text-pollen-dark">
                  <w.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="font-serif text-xl font-semibold text-charcoal">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{w.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-garden-soft/60 py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-5">
            <SectionHead
              eyebrow="Volunteer"
              title="Lend your gifts"
              text="Tell us how you would love to serve. No experience is required for most roles — just a willing heart."
            />
          </div>
          <Reveal className="lg:col-span-7">
            <form onSubmit={onVolunteer} className="card-soft" data-testid="volunteer-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="vol-name" className="label-field">Full name</label>
                  <input id="vol-name" required className="input-field" value={vForm.name} onChange={(e) => setVForm({ ...vForm, name: e.target.value })} data-testid="volunteer-name-input" />
                </div>
                <div>
                  <label htmlFor="vol-email" className="label-field">Email</label>
                  <input id="vol-email" type="email" required className="input-field" value={vForm.email} onChange={(e) => setVForm({ ...vForm, email: e.target.value })} data-testid="volunteer-email-input" />
                </div>
                <div>
                  <label htmlFor="vol-phone" className="label-field">Phone (optional)</label>
                  <input id="vol-phone" className="input-field" value={vForm.phone} onChange={(e) => setVForm({ ...vForm, phone: e.target.value })} data-testid="volunteer-phone-input" />
                </div>
                <div>
                  <label htmlFor="vol-avail" className="label-field">Availability</label>
                  <select id="vol-avail" className="input-field" value={vForm.availability} onChange={(e) => setVForm({ ...vForm, availability: e.target.value })} data-testid="volunteer-availability-select">
                    <option value="">Select…</option>
                    <option>Weekday mornings</option>
                    <option>Weekday evenings</option>
                    <option>Weekends</option>
                    <option>Flexible</option>
                  </select>
                </div>
              </div>
              <fieldset className="mt-5">
                <legend className="label-field">Where would you love to serve?</legend>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRole(r)}
                      aria-pressed={roles.includes(r)}
                      data-testid={`volunteer-role-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors duration-200 ${
                        roles.includes(r) ? "border-garden bg-garden text-white" : "border-garden/25 bg-white text-charcoal/70 hover:border-garden"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </fieldset>
              <div className="mt-5">
                <label htmlFor="vol-message" className="label-field">Anything we should know? (optional)</label>
                <textarea id="vol-message" rows={3} className="input-field" value={vForm.message} onChange={(e) => setVForm({ ...vForm, message: e.target.value })} data-testid="volunteer-message-input" />
              </div>
              <button type="submit" disabled={volunteer.loading} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="volunteer-submit-btn">
                {volunteer.loading ? "Sending…" : "I Want to Volunteer"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <section id="apply" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead
              eyebrow="Participant Application"
              title="Begin your journey"
              text="This is a gentle first step, not a test. Share as much or as little as you wish — a caring team member will reach out at your pace. Everything you share is held in confidence."
            />
            <Reveal delay={0.15}>
              <p className="mt-4 rounded-2xl border border-wing/20 bg-wing-soft/50 p-5 text-sm leading-relaxed text-charcoal/75">
                You are not alone, and you are not too far gone. If you are in immediate danger, please call 911 or the
                resources on our <a href="/resources" className="font-bold text-wing underline">Resources page</a> first.
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-7">
            <form onSubmit={onApply} className="card-soft !border-wing/20" data-testid="application-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="app-name" className="label-field">Your name</label>
                  <input id="app-name" required className="input-field" value={aForm.name} onChange={(e) => setAForm({ ...aForm, name: e.target.value })} data-testid="application-name-input" />
                </div>
                <div>
                  <label htmlFor="app-email" className="label-field">Email</label>
                  <input id="app-email" type="email" required className="input-field" value={aForm.email} onChange={(e) => setAForm({ ...aForm, email: e.target.value })} data-testid="application-email-input" />
                </div>
                <div>
                  <label htmlFor="app-phone" className="label-field">Phone (optional)</label>
                  <input id="app-phone" className="input-field" value={aForm.phone} onChange={(e) => setAForm({ ...aForm, phone: e.target.value })} data-testid="application-phone-input" />
                </div>
                <div>
                  <label htmlFor="app-heard" className="label-field">How did you hear about us?</label>
                  <select id="app-heard" className="input-field" value={aForm.heard} onChange={(e) => setAForm({ ...aForm, heard: e.target.value })} data-testid="application-heard-select">
                    <option value="">Select…</option>
                    <option>Friend or family</option>
                    <option>Church community</option>
                    <option>Counselor or social worker</option>
                    <option>Online search</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="app-situation" className="label-field">Share a little about where you are right now (optional)</label>
                <textarea id="app-situation" rows={4} className="input-field" placeholder="Only what you feel comfortable sharing…" value={aForm.situation} onChange={(e) => setAForm({ ...aForm, situation: e.target.value })} data-testid="application-situation-input" />
              </div>
              <label className="mt-5 flex items-start gap-3 text-sm text-charcoal/75">
                <input type="checkbox" checked={aForm.safeContact} onChange={(e) => setAForm({ ...aForm, safeContact: e.target.checked })} className="mt-1 h-4 w-4 accent-wing" data-testid="application-safe-contact-checkbox" />
                It is safe to contact me at the email or phone I provided.
              </label>
              <button type="submit" disabled={application.loading} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="application-submit-btn">
                {application.loading ? "Sending…" : "Submit My Application"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
